import i18n from "../i18n";

function toCamelCase(str) {
  return str.replace(/([-_][a-z])/gi, (s) =>
    s.toUpperCase().replace("-", "").replace("_", ""),
  );
}

function keysToCamel(o) {
  if (Array.isArray(o)) {
    return o.map((v) => keysToCamel(v));
  } else if (o !== null && typeof o === "object" && o.constructor === Object) {
    return Object.keys(o).reduce((acc, key) => {
      acc[toCamelCase(key)] = keysToCamel(o[key]);
      return acc;
    }, {});
  }
  return o;
}

// Єдина база API. Змінюється через REACT_APP_API_BASE у .env
const BASE_URL =
  process.env.REACT_APP_API_BASE || "http://localhost:5001/apiV1";

const request = async (method, url, data = null) => {
  const config = {
    method,
    headers: {
      "Content-Type": "application/json",
      // Мова інтерфейсу -> сервер віддає повідомлення/помилки цією мовою
      "Accept-Language": i18n.language || "pl",
    },
    credentials: "include",
  };

  if (data !== null && data !== undefined) {
    config.body = JSON.stringify(data);
  }

  const response = await fetch(`${BASE_URL}${url}`, config);

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const err = new Error(body.error || i18n.t("common.networkError"));
    err.status = response.status; // типізована помилка: перевіряй e.status
    if (body.fields) err.fields = body.fields; // помилки по полях для inline-підсвітки
    if (body.field) err.field = body.field; // одиночне поле (напр. slug)
    throw err;
  }

  const raw = await response.json().catch(() => null);
  return keysToCamel(raw);
};

// Заглушка для функцій, яких ще немає на бекенді (OTP, Stripe).
// Показує зрозумілий алерт замість падіння в 404.
const notImplemented = (feature) => {
  const msg = i18n.t("stub.inProgress", { feature });
  if (typeof window !== "undefined") window.alert(msg);
  return Promise.reject(new Error(msg));
};

export const api = {
  // --- Auth ---
  login: (identifier, password) =>
    request("POST", "/auth/login", { identifier, password }),
  logout: () => request("POST", "/auth/logout"),
  getCurrentUser: () => request("GET", "/users/me"),
  register: (payload) => request("POST", "/auth/register", payload),
  registerBusiness: (payload) =>
    request("POST", "/auth/register-business", payload),
  requestOtp: (phone, purpose) =>
    request("POST", "/auth/request-otp", { phone, purpose }),
  verifyOtp: (payload) => request("POST", "/auth/verify-otp", payload),

  // --- Workspaces (B2B) ---
  getPublicWorkspace: (slug) => request("GET", `/workspaces/public/${slug}`),
  getPublicWorkspaceServices: (slug) =>
    request("GET", `/workspaces/public/${slug}/services`),

  // --- Маркетплейс (discovery) ---
  discover: (params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== "" && v != null)
    ).toString();
    return request("GET", `/discover${qs ? `?${qs}` : ""}`);
  },
  getDiscoverFacets: () => request("GET", `/discover/facets`),

  // --- Відгуки салону ---
  getWorkspaceReviews: (slug) => request("GET", `/workspaces/public/${slug}/reviews`),
  getMyReview: (workspaceId) => request("GET", `/workspaces/${workspaceId}/reviews/mine`),
  createReview: (workspaceId, data) => request("POST", `/workspaces/${workspaceId}/reviews`, data),
  moderateReview: (workspaceId, reviewId, status) =>
    request("PATCH", `/workspaces/${workspaceId}/reviews/${reviewId}`, { status }),

  // --- Профіль салону (каталог) ---
  getWorkspaceProfile: (workspaceId) => request("GET", `/workspaces/${workspaceId}/profile`),
  updateWorkspaceProfile: (workspaceId, data) =>
    request("PATCH", `/workspaces/${workspaceId}/profile`, data),

  // --- Білінг (SaaS-підписка платформи) ---
  getBillingPlans: () => request("GET", `/billing/plans`),
  getWorkspaceBilling: (workspaceId) => request("GET", `/workspaces/${workspaceId}/billing`),
  getPaddleCheckout: (workspaceId) => request("GET", `/workspaces/${workspaceId}/billing/checkout`),
  getPaddlePortal: (workspaceId) => request("GET", `/workspaces/${workspaceId}/billing/portal`),
  getAdminBilling: () => request("GET", `/billing/admin`),
  updateAdminBilling: (workspaceId, data) => request("POST", `/billing/admin/${workspaceId}`, data),
  // Завантаження зображення салону (multipart) — повертає { url }
  uploadWorkspaceImage: async (workspaceId, file) => {
    const fd = new FormData();
    fd.append("image", file);
    const res = await fetch(`${BASE_URL}/workspaces/${workspaceId}/upload`, {
      method: "POST",
      body: fd,
      credentials: "include",
      headers: { "Accept-Language": i18n.language || "pl" },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || i18n.t("common.networkError"));
      err.status = res.status;
      throw err;
    }
    return res.json();
  },
  getMyWorkspaces: () => request("GET", "/workspaces/mine"),
  createWorkspace: (data) => request("POST", "/workspaces", data),
  switchWorkspace: (id) => request("POST", `/workspaces/${id}/switch`),
  // Команда воркспейсу
  getWorkspaceMembers: (workspaceId) =>
    request("GET", `/workspaces/${workspaceId}/members`),
  inviteMember: (workspaceId, data) =>
    request("POST", `/workspaces/${workspaceId}/members`, data),
  updateMember: (workspaceId, userId, data) =>
    request("PATCH", `/workspaces/${workspaceId}/members/${userId}`, data),
  removeMember: (workspaceId, userId) =>
    request("DELETE", `/workspaces/${workspaceId}/members/${userId}`),
  // Клієнти салону (статус/нотатки/історія — приватні на воркспейс)
  getWorkspaceClients: (workspaceId) =>
    request("GET", `/workspaces/${workspaceId}/clients`),
  getWorkspaceClient: (workspaceId, userId) =>
    request("GET", `/workspaces/${workspaceId}/clients/${userId}`),
  updateWorkspaceClient: (workspaceId, userId, data) =>
    request("PATCH", `/workspaces/${workspaceId}/clients/${userId}`, data),
  // Налаштування салону (тема / мова)
  getWorkspaceSettings: (workspaceId) =>
    request("GET", `/workspaces/${workspaceId}/settings`),
  updateWorkspaceSettings: (workspaceId, data) =>
    request("PATCH", `/workspaces/${workspaceId}/settings`, data),
  // Правила бронювання салону (Regulamin)
  getWorkspaceTermsPublic: (workspaceId) =>
    request("GET", `/workspaces/${workspaceId}/public-terms`),
  getWorkspaceTerms: (workspaceId) =>
    request("GET", `/workspaces/${workspaceId}/terms`),
  updateWorkspaceTerms: (workspaceId, text) =>
    request("PUT", `/workspaces/${workspaceId}/terms`, { text }),

  // --- Platform admin (superadmin / God) ---
  getAdminWorkspaces: (status) =>
    request(
      "GET",
      status ? `/admin/workspaces?status=${status}` : "/admin/workspaces"
    ),
  setWorkspaceStatus: (id, status) =>
    request("PATCH", `/admin/workspaces/${id}/status`, { status }),
  getAdminStats: () => request("GET", "/admin/stats"),
  getAdminUsers: (role, q) => {
    const p = new URLSearchParams();
    if (role) p.set("role", role);
    if (q) p.set("q", q);
    const qs = p.toString();
    return request("GET", `/admin/users${qs ? `?${qs}` : ""}`);
  },

  // --- Bookings ---
  getBookings: () => request("GET", "/bookings"),
  getBookingById: (id) => request("GET", `/bookings/${id}`),
  createBooking: (data) => request("POST", "/bookings", data),
  cancelBooking: (id) => request("DELETE", `/bookings/${id}`),
  updateBooking: (id, data) => request("PATCH", `/bookings/${id}`, data),
  checkoutBooking: (id, payload) =>
    request("POST", `/bookings/${id}/checkout`, payload),
  createBookingLink: (params) =>
    request("POST", "/bookings/booking-links", params),
  notifyClient: (id, payload) => request("POST", `/bookings/${id}/notify`, payload),

  // --- Promo codes ---
  getPromos: () => request("GET", "/promos"),
  createPromo: (data) => request("POST", "/promos", data),
  updatePromo: (id, data) => request("PATCH", `/promos/${id}`, data),
  deletePromo: (id) => request("DELETE", `/promos/${id}`),
  validatePromo: (masterId, code, amount) =>
    request("GET", `/promos/validate?masterId=${masterId}&code=${encodeURIComponent(code)}&amount=${amount || 0}`),

  // --- Розклад: редагування блоку і per-date відкриті години ---
  updateTimeOff: (id, payload) => request("PATCH", `/schedule/time-off/${id}`, payload),
  setDayOverride: (payload) => request("POST", "/schedule/day-override", payload),
  deleteDayOverride: (id) => request("DELETE", `/schedule/day-override/${id}`),
  getBookingLink: (hash) => request("GET", `/bookings/booking-links/${hash}`),
  getBookingConfirm: (hash) => request("GET", `/bookings/confirm/${hash}`),
  submitBookingConfirm: (hash, payload) =>
    request("POST", `/bookings/confirm/${hash}`, payload),
  getAvailability: (masterId, date, services, workspaceId) =>
    request(
      "GET",
      `/bookings/availability?masterId=${masterId}&date=${date}&services=${(services || []).join(",")}` +
        (workspaceId ? `&workspaceId=${workspaceId}` : ""),
    ),

  // --- Services ---
  getServices: (masterId, workspaceId) =>
    request(
      "GET",
      masterId
        ? `/services?masterId=${masterId}` +
            (workspaceId ? `&workspaceId=${workspaceId}` : "")
        : "/services",
    ),
  createService: (data) => request("POST", "/services", data),
  updateService: (id, data) => request("PATCH", `/services/${id}`, data),
  deleteService: (id) => request("DELETE", `/services/${id}`),

  // --- Users ---
  getMasters: () => request("GET", "/users/masters"),
  getUsers: () => request("GET", "/users"),
  getUsersByOrg: () => request("GET", "/users"),
  createUser: (data) => request("POST", "/users", data),
  updateUser: (id, data) => request("PATCH", `/users/${id}`, data),
  // Завантаження аватара (multipart) — окремо від JSON-хелпера
  uploadAvatar: async (file) => {
    const fd = new FormData();
    fd.append("avatar", file);
    const res = await fetch(`${BASE_URL}/users/me/avatar`, {
      method: "POST",
      body: fd, // Content-Type виставляє браузер (з boundary)
      credentials: "include",
      headers: { "Accept-Language": i18n.language || "pl" },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || i18n.t("common.networkError"));
      err.status = res.status;
      throw err;
    }
    return res.json();
  },
  updateUserStatus: (id, status) =>
    request("PATCH", `/users/${id}/status`, { status }),
  getUserByPhone: (phone) => request("GET", `/users/phone/${phone}`),
  getUserByEmail: (email) => request("GET", `/users/email/${email}`),
  getUserById: (id) => request("GET", `/users/id/${id}`),

  // --- Schedule (розклад майстра) ---
  getSchedule: (masterId) =>
    request("GET", `/schedule${masterId ? `?masterId=${masterId}` : ""}`),
  updateWorkHours: (workHours, masterId) =>
    request("PUT", "/schedule/work-hours", { workHours, masterId }),
  addTimeOff: (payload) => request("POST", "/schedule/time-off", payload),
  deleteTimeOff: (id) => request("DELETE", `/schedule/time-off/${id}`),
  getPublicSchedule: (masterId, workspaceId) =>
    request(
      "GET",
      `/schedule/public?masterId=${masterId}` +
        (workspaceId ? `&workspaceId=${workspaceId}` : ""),
    ),

  // --- Checkouts ---
  getCheckouts: () => request("GET", "/checkouts"),
  getCheckoutsByBookingId: (id) =>
    request("GET", `/checkouts/checkout-by-booking-id/${id}`),

  // --- Payments (є на бекенді) ---
  createPaymentLink: (params) =>
    request("POST", "/payments/payment-links", params),
  getPaymentLink: (hash) => request("GET", `/payments/payment-links/${hash}`),
  confirmPayment: (hash, payload) =>
    request("POST", `/payments/payment-links/${hash}/confirm`, payload),

  // --- Заглушки (ендпоінтів ще немає на бекенді) ---
  createStripeAccount: () => notImplemented(i18n.t("stub.stripeConnect")),
  removeStripeAccount: () => notImplemented(i18n.t("stub.stripeDisconnect")),
  getStripeSetupLink: () => notImplemented(i18n.t("stub.stripeSetup")),
};
